import { SingleValue, AsyncSelect, ChakraStylesConfig } from "chakra-react-select";
import clsx from "clsx";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { debounce } from "lodash";
import { useState } from "react";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";

type LocationSuggestion = {
  locationId: string;
  label: string;
};

export type LocationDetails = {
  id: string;
  displayName: string;
  label?: string;
  timezone?: string;
  coordinates: {
    latitude: string;
    longitude: string;
  };
  addressComponents: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
};

function LocationSelect({
  onChange,
  className = undefined,
  containerStyles = {},
  initialValue = undefined,
  placeholder,
  chakraStyles: externalChakraStyles = {},
}: {
  onChange: (value: LocationDetails) => void;
  className?: string;
  containerStyles?: object;
  initialValue?: LocationSuggestion | undefined;
  placeholder?: string;
  chakraStyles?: ChakraStylesConfig<LocationSuggestion>;
}) {
  const [selectedValue, setSelectedValue] = useState(initialValue || null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const loadOptions = debounce((inputValue: string, callback: (options: LocationSuggestion[]) => void) => {
    if (!inputValue) return callback([]);

    apiClient
      .get<LocationSuggestion[]>(`${API_ROUTES.autocomplete.locations.index}?input=${inputValue}`)
      .then(callback);
  }, 400);

  const handleChange = async (selectedOption: SingleValue<LocationSuggestion>) => {
    setSelectedValue(selectedOption);
    setMenuIsOpen(false);
    if (!selectedOption) return;
    try {
      const location = await apiClient.get<LocationDetails>(
        generatePath(API_ROUTES.autocomplete.locations.show, { id: selectedOption.locationId }),
      );
      onChange({ ...location, label: selectedOption.label });
    } catch (error) {
      console.error("Error fetching details:", error);
    }
  };

  return (
    <AsyncSelect<LocationSuggestion>
      cacheOptions
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.locationId}
      value={selectedValue}
      loadOptions={loadOptions}
      onChange={handleChange}
      onInputChange={(inputValue) => setMenuIsOpen(!!inputValue && isFocused)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      menuIsOpen={menuIsOpen}
      placeholder={placeholder ?? "Search for a location"}
      components={{ DropdownIndicator: null }}
      useBasicStyles
      classNamePrefix="chakra-react-select"
      className={clsx(
        "chakra-react-select-container-single-select",
        {
          "chakra-react-select--has-value": !!selectedValue,
        },
        "location",
        className,
      )}
      colorScheme="brand"
      chakraStyles={{
        container: (provided, state) => {
          const base = { ...provided, cursor: "pointer", ...containerStyles };
          return externalChakraStyles.container ? externalChakraStyles.container(base, state) : base;
        },
        option: (provided, state) => {
          const base = { ...provided, color: "fg.muted" };
          return externalChakraStyles.option ? externalChakraStyles.option(base, state) : base;
        },
        valueContainer: (provided, state) => {
          const base = { ...provided, className: "single-select" };
          return externalChakraStyles.valueContainer ? externalChakraStyles.valueContainer(base, state) : base;
        },
        menu: (provided, state) => {
          const base = { ...provided, marginTop: 0 };
          return externalChakraStyles.menu ? externalChakraStyles.menu(base, state) : base;
        },
        clearIndicator: (provided, state) => {
          const base = {
            ...provided,
            width: "fit-content",
            height: "fit-content",
            marginRight: "1rem",
            _hover: { color: "cdsGrey.900" },
          };
          return externalChakraStyles.clearIndicator ? externalChakraStyles.clearIndicator(base, state) : base;
        },
        crossIcon: (provided, state) => {
          const base = { ...provided, width: "12px", height: "12px" };
          return externalChakraStyles.crossIcon ? externalChakraStyles.crossIcon(base, state) : base;
        },
        ...(externalChakraStyles.control ? { control: externalChakraStyles.control } : {}),
        ...(externalChakraStyles.singleValue ? { singleValue: externalChakraStyles.singleValue } : {}),
      }}
      styles={{
        menuPortal: (provided) => ({ ...provided, zIndex: 1500 }),
        valueContainer: (provided) => ({
          ...provided,
          className: "single-select",
        }),
      }}
      // Not sure if we need all of these
      menuPortalTarget={document.body}
    />
  );
}

export default LocationSelect;
